import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Edit2, Trash2, Users, FileDown, CheckCircle2, Save, X, Lock } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Textarea } from '../../../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import ReportWorkflowTrail from '../../reports/ReportWorkflowTrail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SupplierListTab({ suppliers, atcCodes, onRefresh, fmt, isReadOnly = false }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredSuppliers, setFilteredSuppliers] = useState(suppliers);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showInactive, setShowInactive] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    
    const [form, setForm] = useState({
        name: '',
        contact_person: '',
        address: '',
        tin: '',
        terms: '',
        credit_limit: 0,
        is_vatable: false,
        has_wtax: false,
        atc_1: '',
        atc_2: '',
        atc_3: '',
        atc_4: '',
        emails: '',
        phones: '',
        active_subsidiaries: []
    });

    const getAtcSummary = (supplier) => {
        const codes = [supplier.atc_1, supplier.atc_2, supplier.atc_3, supplier.atc_4].filter(Boolean);
        return codes.length ? codes.join(', ') : '-';
    };
    
    // Update filtered suppliers when suppliers or search changes
    useEffect(() => {
        let filtered = suppliers;
        
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(s => 
                s.name?.toLowerCase().includes(query) ||
                s.supplier_id?.toLowerCase().includes(query) ||
                s.tin?.toLowerCase().includes(query) ||
                s.contact_person?.toLowerCase().includes(query)
            );
        }
        
        if (!showInactive) {
            filtered = filtered.filter(s => s.is_active !== false);
        }
        
        setFilteredSuppliers(filtered);
    }, [suppliers, searchQuery, showInactive]);
    
    // Reset form
    const resetForm = () => {
        setForm({
            name: '',
            contact_person: '',
            address: '',
            tin: '',
            terms: '',
            credit_limit: 0,
            is_vatable: false,
            has_wtax: false,
            atc_1: '',
            atc_2: '',
            atc_3: '',
            atc_4: '',
            emails: '',
            phones: '',
            active_subsidiaries: []
        });
        setSelectedSupplier(null);
        setIsEditing(false);
    };
    
    // Handle Add new supplier
    const handleAdd = () => {
        if (isReadOnly) {
            toast.error('Supplier Master is read-only in this review scope.');
            return;
        }
        resetForm();
        setIsEditing(true);
    };
    
    // Handle Edit supplier
    const handleEdit = (supplier) => {
        if (isReadOnly) {
            toast.error('Supplier Master is read-only in this review scope.');
            return;
        }
        setSelectedSupplier(supplier);
        setForm({
            name: supplier.name || '',
            contact_person: supplier.contact_person || '',
            address: supplier.address || '',
            tin: supplier.tin || '',
            terms: supplier.terms || '',
            credit_limit: supplier.credit_limit || 0,
            is_vatable: supplier.is_vatable || false,
            has_wtax: supplier.has_wtax || false,
            atc_1: supplier.atc_1 || '',
            atc_2: supplier.atc_2 || '',
            atc_3: supplier.atc_3 || '',
            atc_4: supplier.atc_4 || '',
            emails: (supplier.emails || []).join(', '),
            phones: (supplier.phones || []).join(', '),
            active_subsidiaries: supplier.active_subsidiaries || []
        });
        setIsEditing(true);
    };
    
    // Handle Cancel
    const handleCancel = () => {
        resetForm();
    };
    
    // Handle Save
    const handleSave = async () => {
        if (isReadOnly) {
            toast.error('Supplier Master is read-only in this review scope.');
            return;
        }
        // Validation
        if (!form.name.trim()) {
            toast.error('Supplier name is required');
            return;
        }
        
        if ((form.is_vatable || form.has_wtax) && !form.tin.trim()) {
            toast.error('TIN is required when VATABLE or WTAX is enabled');
            return;
        }
        
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                contact_person: form.contact_person.trim() || null,
                address: form.address.trim() || null,
                tin: form.tin.trim() || null,
                terms: form.terms.trim() || null,
                credit_limit: parseFloat(form.credit_limit) || 0,
                is_vatable: form.is_vatable,
                has_wtax: form.has_wtax,
                atc_1: form.atc_1 || null,
                atc_2: form.atc_2 || null,
                atc_3: form.atc_3 || null,
                atc_4: form.atc_4 || null,
                emails: form.emails ? form.emails.split(',').map(e => e.trim()).filter(Boolean) : [],
                phones: form.phones ? form.phones.split(',').map(p => p.trim()).filter(Boolean) : [],
                active_subsidiaries: form.active_subsidiaries
            };
            
            if (selectedSupplier) {
                await axios.put(`${API}/suppliers/${selectedSupplier.id}`, payload);
                setLastResult({
                    title: `Supplier ${selectedSupplier.supplier_id} updated`,
                    status: 'Active',
                    source: payload.name,
                    reference: payload.tin || payload.terms || 'Supplier profile',
                    location: 'Setup > Supplier Master and AP supplier dropdowns',
                    reviewState: 'Available for AP Voucher, AP Payment, and supplier reports.',
                    glImpact: 'Master-data only. No GL, Trial Balance, AP balances, or reports change until used by a posted transaction.',
                    nextSafeAction: 'Use this supplier on AP transactions only after confirming TIN, VAT/WTAX, terms, and contact details.'
                });
                toast.success(
                    `Supplier ${selectedSupplier.supplier_id} updated. Find it in Setup > Supplier Master and AP supplier dropdowns. No GL or Trial Balance impact until used in a posted transaction.`
                );
            } else {
                const response = await axios.post(`${API}/suppliers`, payload);
                const supplierId = response.data?.supplier_id || response.data?.id || 'new supplier';
                setLastResult({
                    title: `Supplier ${supplierId} created`,
                    status: 'Active',
                    source: payload.name,
                    reference: payload.tin || payload.terms || 'Supplier profile',
                    location: 'Setup > Supplier Master and AP supplier dropdowns',
                    reviewState: 'Available for AP Voucher, AP Payment, and supplier reports.',
                    glImpact: 'Master-data only. No GL, Trial Balance, AP balances, or reports change until used by a posted transaction.',
                    nextSafeAction: 'Use this supplier on AP transactions only after confirming TIN, VAT/WTAX, terms, and contact details.'
                });
                toast.success(
                    `Supplier ${supplierId} created. Find it in Setup > Supplier Master and AP supplier dropdowns. No GL or Trial Balance impact until used in a posted transaction.`
                );
            }
            
            resetForm();
            onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save supplier');
        }
        setSaving(false);
    };
    
    // Handle Delete
    const handleDelete = async (supplier) => {
        if (isReadOnly) {
            toast.error('Supplier Master is read-only in this review scope.');
            return;
        }
        if (!window.confirm(`Are you sure you want to deactivate supplier "${supplier.name}"?`)) {
            return;
        }
        
        try {
            await axios.delete(`${API}/suppliers/${supplier.id}`);
            setLastResult({
                title: `Supplier ${supplier.supplier_id} deactivated`,
                status: 'Inactive',
                source: supplier.name,
                reference: supplier.tin || supplier.terms || 'Supplier profile',
                location: 'Setup > Supplier Master when Show Inactive is enabled',
                reviewState: 'Hidden from normal active supplier selection; historical AP records remain linked.',
                glImpact: 'Master-data status only. Existing posted AP/GL/Trial Balance evidence is preserved.',
                nextSafeAction: 'Reactivate only if this supplier should be available for new AP transactions again.'
            });
            toast.success(
                `Supplier ${supplier.supplier_id} deactivated. It stays in Supplier Master when Show Inactive is on; existing AP and GL history remains preserved.`
            );
            onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete supplier');
        }
    };
    
    // Handle Activate
    const handleActivate = async (supplier) => {
        if (isReadOnly) {
            toast.error('Supplier Master is read-only in this review scope.');
            return;
        }
        try {
            await axios.put(`${API}/suppliers/${supplier.id}/activate`);
            setLastResult({
                title: `Supplier ${supplier.supplier_id} activated`,
                status: 'Active',
                source: supplier.name,
                reference: supplier.tin || supplier.terms || 'Supplier profile',
                location: 'Setup > Supplier Master and AP supplier dropdowns',
                reviewState: 'Available again for AP Voucher, AP Payment, and supplier reports.',
                glImpact: 'Master-data status only. No GL, Trial Balance, AP balances, or reports change until used by a posted transaction.',
                nextSafeAction: 'Confirm details before using this supplier on new AP transactions.'
            });
            toast.success(
                `Supplier ${supplier.supplier_id} activated. It is available again in Setup > Supplier Master and AP supplier dropdowns.`
            );
            onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to activate supplier');
        }
    };
    
    // Handle Export to XLS
    const handleExport = async () => {
        try {
            const res = await axios.get(`${API}/suppliers/export/xls`);
            const data = res.data.data;
            
            // Convert to CSV for download
            if (data.length === 0) {
                toast.error('No data to export');
                return;
            }
            
            const headers = Object.keys(data[0]);
            const csvContent = [
                headers.join(','),
                ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
            ].join('\n');
            
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = res.data.filename.replace('.xlsx', '.csv');
            link.click();
            
            toast.success(`Exported ${data.length} suppliers. Export only; no AP, GL, or Trial Balance records changed.`);
        } catch (error) {
            toast.error('Failed to export suppliers');
        }
    };
    
    return (
        <div className="space-y-4" data-testid="supplier-list-tab">
            {isReadOnly && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="supplier-master-readonly-scope">
                    Supplier Master is read-only in this review scope. Use it to confirm AP source setup; changes should be made only from an editable workspace.
                </div>
            )}

            <ReportWorkflowTrail
                title="Supplier Master trail"
                testId="supplier-master-workflow-trail"
                scope={isReadOnly ? 'Read-only review' : 'Your setup workspace'}
                items={[
                    {
                        label: 'Source setup',
                        value: 'Suppliers define AP counterparties, payment terms, VAT/WTAX flags, and contact details.'
                    },
                    {
                        label: 'Where it goes',
                        value: 'Active suppliers appear in Setup > Supplier Master and AP Voucher/AP Payment supplier selectors.'
                    },
                    {
                        label: 'Review',
                        value: 'Check TIN, terms, VAT/WTAX, ATC, and contact details before using a supplier in AP transactions.'
                    },
                    {
                        label: 'GL and TB',
                        value: 'Supplier records alone do not affect GL or Trial Balance; only posted AP transactions do.'
                    },
                    {
                        label: 'Next safe action',
                        value: 'Use deactivate for obsolete suppliers. Existing posted AP evidence remains linked and preserved.'
                    }
                ]}
            />

            {lastResult && (
                <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-950" data-testid="supplier-master-result-trail">
                    <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between">
                        <div>
                            <p className="font-semibold">{lastResult.title}</p>
                            <p className="mt-1 text-sky-800">Status: <span className="font-semibold">{lastResult.status}</span></p>
                        </div>
                        <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-semibold text-sky-700">
                            Latest setup action
                        </span>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                        <p><span className="font-semibold">Created/changed:</span> {lastResult.source}</p>
                        <p><span className="font-semibold">Reference:</span> {lastResult.reference}</p>
                        <p><span className="font-semibold">Find it now:</span> {lastResult.location}</p>
                        <p><span className="font-semibold">Review:</span> {lastResult.reviewState}</p>
                        <p><span className="font-semibold">Accounting impact:</span> {lastResult.glImpact}</p>
                        <p><span className="font-semibold">Next safe action:</span> {lastResult.nextSafeAction}</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-[#0F2A44] to-[#1E3A8A] px-6 py-4 text-white">
                    <h2 className="font-semibold font-['Manrope'] flex items-center gap-2">
                        <Users className="w-5 h-5" /> Supplier Master List
                    </h2>
                    <p className="text-white/70 text-sm mt-1">Manage suppliers for AP, JV, and reports</p>
                </div>
                
                {/* Search & Actions */}
                <div className="p-4 border-b border-slate-200 flex items-center gap-4 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                placeholder="Search by name, ID, TIN, contact..."
                                className="pl-10"
                                data-testid="supplier-search"
                            />
                        </div>
                    </div>
                    
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                        <input
                            type="checkbox"
                            checked={showInactive}
                            onChange={e => setShowInactive(e.target.checked)}
                            className="rounded border-slate-300"
                        />
                        Show Inactive
                    </label>
                    
                    <Button variant="outline" onClick={handleExport} data-testid="export-btn">
                        <FileDown className="w-4 h-4 mr-2" /> Export XLS
                    </Button>
                    
                    {!isEditing && !isReadOnly && (
                        <Button onClick={handleAdd} className="bg-[#2F8FCB] hover:bg-[#2577a8]" data-testid="add-supplier-btn">
                            <Plus className="w-4 h-4 mr-2" /> Add Supplier
                        </Button>
                    )}
                </div>
                
                {/* Edit Form */}
                {isEditing && (
                    <div className="p-6 border-b border-slate-200 bg-blue-50">
                        <h3 className="font-semibold text-slate-900 mb-4">
                            {selectedSupplier ? `Edit Supplier: ${selectedSupplier.supplier_id}` : 'New Supplier'}
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <Label>Supplier ID</Label>
                                <Input
                                    value={selectedSupplier?.supplier_id || '(Auto-generated)'}
                                    disabled
                                    className="mt-1 bg-slate-100"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <Label>Name <span className="text-red-500">*</span></Label>
                                <Input
                                    value={form.name}
                                    onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Supplier name"
                                    className="mt-1"
                                    data-testid="supplier-name"
                                />
                            </div>
                            
                            <div>
                                <Label>Contact Person</Label>
                                <Input
                                    value={form.contact_person}
                                    onChange={e => setForm(prev => ({ ...prev, contact_person: e.target.value }))}
                                    placeholder="Contact name"
                                    className="mt-1"
                                    data-testid="supplier-contact"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <Label>Address</Label>
                                <Input
                                    value={form.address}
                                    onChange={e => setForm(prev => ({ ...prev, address: e.target.value }))}
                                    placeholder="Full address"
                                    className="mt-1"
                                    data-testid="supplier-address"
                                />
                            </div>
                            
                            <div>
                                <Label>TIN {(form.is_vatable || form.has_wtax) && <span className="text-red-500">*</span>}</Label>
                                <Input
                                    value={form.tin}
                                    onChange={e => setForm(prev => ({ ...prev, tin: e.target.value }))}
                                    placeholder="000-000-000-000"
                                    className="mt-1"
                                    data-testid="supplier-tin"
                                />
                            </div>
                            <div>
                                <Label>Terms</Label>
                                <Select
                                    value={form.terms || 'none'}
                                    onValueChange={v => setForm(prev => ({ ...prev, terms: v === 'none' ? '' : v }))}
                                >
                                    <SelectTrigger className="mt-1" data-testid="supplier-terms">
                                        <SelectValue placeholder="Select terms" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">None</SelectItem>
                                        <SelectItem value="COD">COD (Cash on Delivery)</SelectItem>
                                        <SelectItem value="Net 7">Net 7</SelectItem>
                                        <SelectItem value="Net 15">Net 15</SelectItem>
                                        <SelectItem value="Net 30">Net 30</SelectItem>
                                        <SelectItem value="Net 45">Net 45</SelectItem>
                                        <SelectItem value="Net 60">Net 60</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Credit Limit</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={form.credit_limit}
                                    onChange={e => setForm(prev => ({ ...prev, credit_limit: e.target.value }))}
                                    placeholder="0.00"
                                    className="mt-1 font-mono text-right"
                                    data-testid="supplier-credit-limit"
                                />
                            </div>
                            
                            <div className="md:col-span-3 rounded-lg border border-slate-200 bg-white p-4">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                        <h4 className="text-sm font-semibold text-slate-900">BIR / tax profile</h4>
                                        <p className="text-xs text-slate-500 mt-1">Used for AP withholding setup, VAT classification, and future compliance exports.</p>
                                    </div>
                                    <div className="flex items-center gap-6">
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={form.is_vatable}
                                                onChange={e => setForm(prev => ({ ...prev, is_vatable: e.target.checked }))}
                                                className="rounded border-slate-300"
                                                data-testid="supplier-vatable"
                                            />
                                            <span className="text-sm">VATABLE</span>
                                        </label>
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={form.has_wtax}
                                                onChange={e => setForm(prev => ({ ...prev, has_wtax: e.target.checked }))}
                                                className="rounded border-slate-300"
                                                data-testid="supplier-wtax"
                                            />
                                            <span className="text-sm">WTAX</span>
                                        </label>
                                    </div>
                                </div>
                                {(form.is_vatable || form.has_wtax) && (
                                    <p className="mt-3 text-xs font-medium text-amber-700">
                                        TIN is required when VATABLE or WTAX is enabled.
                                    </p>
                                )}
                            </div>
                            
                            {/* ATC Dropdowns */}
                            <div>
                                <Label>ATC 1</Label>
                                <Select
                                    value={form.atc_1 || 'none'}
                                    onValueChange={v => setForm(prev => ({ ...prev, atc_1: v === 'none' ? '' : v }))}
                                >
                                    <SelectTrigger className="mt-1" data-testid="supplier-atc1">
                                        <SelectValue placeholder="Select ATC" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">None</SelectItem>
                                        {atcCodes.map(atc => (
                                            <SelectItem key={atc.code} value={atc.code}>
                                                {atc.code} - {atc.desc} ({atc.rate}%)
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>ATC 2</Label>
                                <Select
                                    value={form.atc_2 || 'none'}
                                    onValueChange={v => setForm(prev => ({ ...prev, atc_2: v === 'none' ? '' : v }))}
                                >
                                    <SelectTrigger className="mt-1" data-testid="supplier-atc2">
                                        <SelectValue placeholder="Select ATC" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">None</SelectItem>
                                        {atcCodes.map(atc => (
                                            <SelectItem key={atc.code} value={atc.code}>
                                                {atc.code} - {atc.desc} ({atc.rate}%)
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div>
                                <Label>ATC 3</Label>
                                <Select
                                    value={form.atc_3 || 'none'}
                                    onValueChange={v => setForm(prev => ({ ...prev, atc_3: v === 'none' ? '' : v }))}
                                >
                                    <SelectTrigger className="mt-1" data-testid="supplier-atc3">
                                        <SelectValue placeholder="Select ATC" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">None</SelectItem>
                                        {atcCodes.map(atc => (
                                            <SelectItem key={atc.code} value={atc.code}>
                                                {atc.code} - {atc.desc} ({atc.rate}%)
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>ATC 4</Label>
                                <Select
                                    value={form.atc_4 || 'none'}
                                    onValueChange={v => setForm(prev => ({ ...prev, atc_4: v === 'none' ? '' : v }))}
                                >
                                    <SelectTrigger className="mt-1" data-testid="supplier-atc4">
                                        <SelectValue placeholder="Select ATC" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">None</SelectItem>
                                        {atcCodes.map(atc => (
                                            <SelectItem key={atc.code} value={atc.code}>
                                                {atc.code} - {atc.desc} ({atc.rate}%)
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div className="md:col-span-2">
                                <Label>Emails (comma-separated)</Label>
                                <Input
                                    value={form.emails}
                                    onChange={e => setForm(prev => ({ ...prev, emails: e.target.value }))}
                                    placeholder="email1@example.com, email2@example.com"
                                    className="mt-1"
                                    data-testid="supplier-emails"
                                />
                            </div>
                            <div>
                                <Label>Phones (comma-separated)</Label>
                                <Input
                                    value={form.phones}
                                    onChange={e => setForm(prev => ({ ...prev, phones: e.target.value }))}
                                    placeholder="+63 xxx xxx xxxx"
                                    className="mt-1"
                                    data-testid="supplier-phones"
                                />
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-3 mt-6">
                            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-supplier-btn">
                                <Save className="w-4 h-4 mr-2" /> {selectedSupplier ? 'Update' : 'Save'}
                            </Button>
                            <Button variant="outline" onClick={handleCancel} disabled={saving} data-testid="cancel-supplier-btn">
                                <X className="w-4 h-4 mr-2" /> Cancel
                            </Button>
                        </div>
                    </div>
                )}
                
                {/* Supplier Grid */}
                {filteredSuppliers.length === 0 ? (
                    <div className="p-12 text-center">
                        <Users className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                        <p className="text-slate-500">No suppliers found</p>
                        <p className="text-sm text-slate-400 mt-1">
                            {isReadOnly ? 'No suppliers are visible in this review scope' : 'Click "Add Supplier" to create your first supplier'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
                                <tr>
                                    <th className="px-4 py-3 text-left">Supplier ID</th>
                                    <th className="px-4 py-3 text-left">Name</th>
                                    <th className="px-4 py-3 text-left">Contact</th>
                                    <th className="px-4 py-3 text-left">TIN</th>
                                    <th className="px-4 py-3 text-left">Terms</th>
                                    <th className="px-4 py-3 text-right">Credit Limit</th>
                                    <th className="px-4 py-3 text-center">VAT</th>
                                    <th className="px-4 py-3 text-center">WTAX</th>
                                    <th className="px-4 py-3 text-left">ATC</th>
                                    <th className="px-4 py-3 text-center">Status</th>
                                    <th className="px-4 py-3 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredSuppliers.map((supplier) => (
                                    <tr key={supplier.id} className={`hover:bg-slate-50 ${!supplier.is_active ? 'opacity-50' : ''}`} data-testid={`supplier-row-${supplier.id}`}>
                                        <td className="px-4 py-3 font-mono text-sm">{supplier.supplier_id}</td>
                                        <td className="px-4 py-3 font-medium">{supplier.name}</td>
                                        <td className="px-4 py-3 text-sm text-slate-600">{supplier.contact_person || '-'}</td>
                                        <td className="px-4 py-3 font-mono text-sm">{supplier.tin || '-'}</td>
                                        <td className="px-4 py-3 text-sm">{supplier.terms || '-'}</td>
                                        <td className="px-4 py-3 text-right font-mono text-sm">{fmt(supplier.credit_limit || 0)}</td>
                                        <td className="px-4 py-3 text-center">
                                            {supplier.is_vatable && <span className="text-emerald-600">✓</span>}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            {supplier.has_wtax && <span className="text-emerald-600">✓</span>}
                                        </td>
                                        <td className="px-4 py-3 text-xs font-mono text-slate-600">{getAtcSummary(supplier)}</td>
                                        <td className="px-4 py-3 text-center">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                supplier.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                                            }`}>
                                                {supplier.is_active !== false ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                {isReadOnly ? (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <button className="p-1.5 text-slate-300 cursor-not-allowed rounded-lg" disabled data-testid={`readonly-supplier-${supplier.id}`}>
                                                                <Lock className="w-4 h-4" />
                                                            </button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Read-only review</TooltipContent>
                                                    </Tooltip>
                                                ) : (
                                                    <>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <button
                                                                    onClick={() => handleEdit(supplier)}
                                                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                                                                    data-testid={`edit-supplier-${supplier.id}`}
                                                                >
                                                                    <Edit2 className="w-4 h-4" />
                                                                </button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Edit</TooltipContent>
                                                        </Tooltip>

                                                        {supplier.is_active !== false ? (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <button
                                                                        onClick={() => handleDelete(supplier)}
                                                                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                                                        data-testid={`delete-supplier-${supplier.id}`}
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Deactivate</TooltipContent>
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <button
                                                                        onClick={() => handleActivate(supplier)}
                                                                        className="p-1.5 text-emerald-500 hover:bg-emerald-50 rounded-lg"
                                                                    >
                                                                        <CheckCircle2 className="w-4 h-4" />
                                                                    </button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Activate</TooltipContent>
                                                            </Tooltip>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                
                {/* Footer Stats */}
                <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 text-sm text-slate-600">
                    Showing {filteredSuppliers.length} of {suppliers.length} suppliers
                </div>
            </div>
        </div>
    );
}

// ================= CUSTOMER LIST TAB =================


export default SupplierListTab;
